import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { debounceTime, delay, filter, map, takeUntil, tap } from "rxjs/operators";
import { CategoriesService } from "../../../shared/services/categories.service";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { ApiService } from "../../../shared/services/api.service";
import { ToastService } from "../../../shared/services/toast.service";
import { BehaviorSubject, Subject } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import lgZoom from 'lightgallery/plugins/zoom';

@Component({
  selector: 'app-edit-category',
  templateUrl: './edit-category.component.html',
  styleUrls: ['./edit-category.component.scss']
})
export class EditCategoryComponent implements OnInit {

  public form: FormGroup;

  public gallerySettings = {
    counter: false,
    plugins: [lgZoom]
  };

  protected content: any = [];
  public contentServerSideFilteringCtrl: FormControl = new FormControl();
  public searching = false;
  public filteredServerSideContent: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  public currentIcon: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public currentPreview: BehaviorSubject<any> = new BehaviorSubject<any>('');

  protected _onDestroy = new Subject<void>();

  public fileName = '';

  private categoryId: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private categoriesService: CategoriesService,
    private fb: FormBuilder,
    private api: ApiService,
    private toast: ToastService,
    private translate: TranslateService
  ) {

    this.form = this.fb.group({
      categoryName: ['', [Validators.required, Validators.minLength(3)]],
      categoryIconSource: '',
      categoryIcon: '',
      imageSelected: ['', Validators.required],
      linkToContent: ['', Validators.required],
      hint: ['', Validators.required],
      sendToAPI: [false, Validators.required],
    });

  }


  ngOnInit(): void {
    // get category data
    this.categoryId = this.route.snapshot.paramMap.get('id');

    if (this.categoryId) {
      this.categoriesService.getCategory(this.categoryId).subscribe({
        next: (resp: any) => {
          this.form.get('categoryName')?.patchValue(resp.name);
          this.form.get('hint')?.patchValue(resp.hint);
          this.form.get('sendToAPI')?.patchValue(resp.sendToAPI);
          this.form.get('linkToContent')?.patchValue(resp.content?.id);
          if (resp.logoUri) {
            this.currentPreview.next(resp.logoUri);
            this.form.get('imageSelected')?.patchValue(resp.logoUri);
          }
        },
        error: (error: any) => {}});
    }

    // get initial content values
    this.categoriesService.searchContentCategory('').subscribe({
      next: (resp: any) => {
        this.filteredServerSideContent.next(resp);
      },
      error: (error: any) => {}});

    // listen for search field value changes
    this.contentServerSideFilteringCtrl.valueChanges
      .pipe(
        filter(search => !!search),
        tap(() => this.searching = true),
        takeUntil(this._onDestroy),
        debounceTime(200),
        map(search => {
          if (!this.content) {
            return [];
          }

          // server fetching and filtering data
          return this.categoriesService.searchContentCategory(search).subscribe({
            next: (resp: any) => {
              this.filteredServerSideContent.next(resp);
              this.searching = false;
            },
            error: (error: any) => {
              this.searching = false;
            }
          })
        }),
        delay(500),
        takeUntil(this._onDestroy)
      )
      .subscribe({});
  }

  public checkError = (controlName: string, errorName: string) => {
    return this.form.controls[controlName].hasError(errorName);
  }

  onFileSelected(event: any) {

    if (event.target.files.length > 0) {
      this.form.patchValue({
        categoryIcon: event.target.files[0]
      });
      this.form.get('imageSelected')?.patchValue(event.target.files[0]);
      this.currentIcon.next(this.form.get('categoryIconSource')?.value);
      this.preparePreview(event.target.files[0]);
    }
  }

  private preparePreview(file: any) {
    let reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (_event) => {
      this.currentPreview.next(reader.result);
    }
  }

  public get iconPreview(): string {
    return this.currentPreview.getValue();
  }

  updateReportCategory() {
    if (this.form.valid) {

      let formData: any = new FormData();
      formData.append('Id', this.categoryId);
      formData.append('Name', this.form.get('categoryName')?.value);
      formData.append('sendToAPI', this.form.get('sendToAPI')?.value);
      if (this.form.get('hint')?.value) formData.append('Hint', this.form.get('hint')?.value);
      if (this.form.get('linkToContent')?.value) formData.append('ContentId', this.form.get('linkToContent')?.value);
      if (this.form.get('categoryIcon')?.value) formData.append('Image', this.form.get('categoryIcon')?.value);

      this.categoriesService.updateCategory(formData).subscribe({
        next: (resp: any) => {
          this.toast.show(this.translate.instant('SUCCESS_MESSAGES.UPDATED_REPORT_CATEGORY'));
          this.router.navigate(['/categories']);
        },
        error: (error) => {},
      });
    }

  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

}
